import React, { Component } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import Plot from "react-plotly.js";
import "./index.css";

const alertingColors = { info: "green", warning: "orange", critical: "red", reportable: "#900C3F" };

class AlertLineGraph extends Component {
  constructor(props) {
    super(props);

    let maxRecords = 10000;
    let seriesIndex = 1;

    if (props.maxRecords !== undefined) {
      maxRecords = props.maxRecords;
    }

    if (props.seriesIndex !== undefined) {
      seriesIndex = props.seriesIndex;
    }

    this.state = {
      time: 0,
      maxRecords: maxRecords,
      seriesIndex: seriesIndex
    };
  }

  getXmin = isItTruncated => {
    let tempXMin = null;

    if (isItTruncated) {
      tempXMin = this.props.filteredDataList[0].rows[0];
    } else {
      tempXMin = this.props.filteredDataList[0].plotting_advice.min;
    }

    return new Date(tempXMin).getTime();
  };

  getXmax = () => {
    let tempXMax = null;
    tempXMax = this.props.filteredDataList[0].plotting_advice.max;
    return new Date(tempXMax).getTime();
  };

  getYmin = () => {
    let tempYMin = null;
    tempYMin = this.props.filteredDataList[1].plotting_advice.min;
    return tempYMin;
  };

  getYmax = () => {
    let tempYMax = null;
    tempYMax = this.props.filteredDataList[1].plotting_advice.max;
    return tempYMax;
  };

  isDataTruncated = () => {
    let tempDataIsTruncated = false;

    // TODO: Use the flag from the server instead of this hack
    if (this.props.filteredDataList[0].rows.length === this.state.maxRecords) {
      tempDataIsTruncated = true;
    }
    return tempDataIsTruncated;
  };

  getSeriesName = () => {
    let tempSeriesName = null;
    tempSeriesName = this.props.filteredDataList[1].name;
    return tempSeriesName;
  };

  getSeriesUnit = () => {
    let tempSeriesUnit = null;
    tempSeriesUnit = this.props.filteredDataList[1].unit;
    return tempSeriesUnit;
  };

  getCustomYTickValues = () => {
    if (!this.props.filteredDataList[1]) {
      return null;
    }
    const tempCustomYTickValues = [];
    _.each(this.props.filteredDataList[1].plotting_advice.yaxis_markers, value => {
      tempCustomYTickValues.push(value[0]);
    });
    return tempCustomYTickValues;
  };

  getCustomYTickText = () => {
    if (!this.props.filteredDataList[1]) {
      return null;
    }
    const tempCustomYTickText = [];
    _.each(this.props.filteredDataList[1].plotting_advice.yaxis_markers, value => {
      tempCustomYTickText.push(value[1]);
    });
    return tempCustomYTickText;
  };

  getGraphAttributes = () => {
    if (!this.props.filteredDataList) {
      return;
    }

    const attributeValues = {
      xMin: this.getXmin(this.isDataTruncated()),
      xMax: this.getXmax(),
      yMin: this.getYmin(),
      yMax: this.getYmax(),
      seriesUnit: this.getSeriesUnit(),
      seriesName: this.getSeriesName(),
      dataIsTruncated: this.isDataTruncated()
    };

    return attributeValues;
  };

  getGraphData = (xMin, xMax) => {
    const tempTimestamps = [];
    const tempValues = [];
    let tempData = {};

    if (this.props.filteredDataList) {
      if (this.props.filteredDataList[1].rows.length > 0) {
        for (let i = 0; i < this.props.filteredDataList[0].rows.length; i++) {
          tempTimestamps.push(new Date(this.props.filteredDataList[0].rows[i]));
          tempValues.push(this.props.filteredDataList[1].rows[i]);
        }
      } else {
        tempTimestamps.push(new Date(xMin));
        tempTimestamps.push(new Date(xMax));
      }
    }

    tempData = {
      timestamps: tempTimestamps,
      values: tempValues
    };

    return tempData;
  };

  getAlertTraceData = () => {
    if (!this.props.telemetryAlertDataList && !this.props.filteredDataList[1]) {
      return null;
    }

    const telemetry_type = this.props.completeSensorDataList[0].telemetry_type;
    const series_id = this.props.filteredDataList[1].id;
    const foundAlertItem = [];
    const tempAlertData = [];

    _.each(this.props.telemetryAlertDataList, value => {
      if (value.monitored_telemetry_type === telemetry_type && value.monitored_series_id === series_id) {
        foundAlertItem.push(value);
      }
    });

    _.each(foundAlertItem, value => {
      if (value.columns[1].rows.length > 0) {
        const tempTimestamps = [];
        const tempValues = [];

        let lastThreshold = null;

        for (let i = 0; i < value.columns[1].rows.length; i++) {
          const timestamp = new Date(value.columns[0].rows[i]);
          const threshold = value.columns[1].rows[i];

          if (lastThreshold !== null && threshold === null) {
            tempTimestamps.push(timestamp);
            tempValues.push(lastThreshold);
          }

          tempTimestamps.push(timestamp);
          tempValues.push(threshold);

          lastThreshold = threshold;
        }

        const alertLineType = this.checkAllTimeStampIsSame(tempTimestamps);

        tempAlertData.push({
          timestamps: tempTimestamps,
          values: tempValues,
          severity: value.alert_type.severity,
          name: value.alert_type.name,
          lineType: alertLineType
        });
      }
    });

    return tempAlertData;
  };

  checkAllTimeStampIsSame = value => {
    if (!value) {
      return null;
    }

    for (let i = 0; i < value.length; i++) {
      const compareDate = new Date(value[0]);
      const newDate = new Date(value[i]);
      if (newDate.getTime() !== compareDate.getTime()) {
        return false;
      }
    }
    return true;
  };

  createAlertLines = alertData => {
    const alertLines = [];

    _.each(alertData, value => {
      const tempAlertObj = {
        type: "scatter",
        mode: value.lineType ? "markers" : "lines",
        x: value.timestamps,
        y: value.values,
        line: { shape: "hv", width: 0.5, color: alertingColors[value.severity], dash: "solid" },
        name: value.name
      };
      alertLines.push(tempAlertObj);
    });

    return alertLines;
  };

  createAlertStartAndEndLines = attributeValue => {
    const lines = [];

    const startLine = {
      type: "line",
      xref: "x",
      yref: "paper",
      x0: this.props.start,
      y0: attributeValue.yMin,
      x1: this.props.start,
      y1: attributeValue.yMax,
      line: {
        width: 1,
        color: "#14ADC7"
      }
    };

    const endLine = {
      type: "line",
      xref: "x",
      yref: "paper",
      x0: this.props.end,
      y0: attributeValue.yMin,
      x1: this.props.end,
      y1: attributeValue.yMax,
      line: {
        width: 1,
        color: "#14ADC7"
      }
    };

    lines.push(startLine);

    if (this.props.closed) {
      lines.push(endLine);
    }

    return lines;
  };

  renderGraphData = (data, attributes, alerts) => {
    if (data.values.length > 0) {
      const tempGraphDataArray = [];

      const sensorData = {
        type: "scatter",
        mode: "lines",
        x: data.timestamps,
        y: data.values,
        line: {
          width: 1,
          color: "#000000"
        },
        name: attributes.seriesName
      };

      const alertData = this.createAlertLines(alerts);

      tempGraphDataArray.push(sensorData);

      _.each(alertData, value => {
        tempGraphDataArray.push(value);
      });

      return tempGraphDataArray;
    } else {
      return [
        {
          x: data.timestamps,
          y: [-1, -1],
          hoverinfo: "none"
        }
      ];
    }
  };

  render = () => {
    const { filteredDataList } = this.props;
    const attributeValue = this.getGraphAttributes();

    if (!(attributeValue !== undefined)) {
      return null;
    }

    const alertData = this.getAlertTraceData();
    const graphData = this.getGraphData(attributeValue.xMin, attributeValue.xMax);
    const alertStartAndEndLines = this.createAlertStartAndEndLines(attributeValue);

    const annotations = [
      {
        x: this.props.start,
        y: 1,
        xref: "x",
        yref: "paper",
        yanchor: "top",
        text: "START",
        textangle: 270,
        xshift: -10,
        yshift: 12,
        showarrow: false,
        align: "right",
        font: {
          size: 10
        }
      }
    ];

    // Only show the end annotation if the alert is closed
    if (this.props.closed) {
      annotations.push({
        x: this.props.end,
        y: 1,
        xref: "x",
        yref: "paper",
        yanchor: "top",
        text: "END",
        textangle: 270,
        xshift: 8,
        yshift: 12,
        showarrow: false,
        align: "right",
        font: {
          size: 10
        }
      });
    }

    return (
      <div className="outer-con">
        <Plot
          style={{
            width: "100%",
            height: this.props.height
          }}
          data={this.renderGraphData(graphData, attributeValue, alertData)}
          layout={{
            shapes: alertStartAndEndLines,
            autosize: true,
            showlegend: false,
            margin: {
              l: 50,
              r: 50,
              b: 50,
              t: 10,
              pad: 8
            },
            yaxis: {
              title: attributeValue.seriesName + " (" + attributeValue.seriesUnit + ")",
              range: [attributeValue.yMin, attributeValue.yMax],
              color: "#888888",
              zerolinecolor: "#dddddd",
              showline: false,
              fixedrange: true,
              automargin: true,
              tickprefix: "  ",
              titlefont: {
                size: 11
              },
              tickvals: filteredDataList[1].plotting_advice.yaxis_markers ? this.getCustomYTickValues() : null,
              ticktext: filteredDataList[1].plotting_advice.yaxis_markers ? this.getCustomYTickText() : null
            },
            xaxis: {
              range: [attributeValue.xMin, attributeValue.xMax],
              color: "#888888",
              zerolinecolor: "#dddddd",
              showline: false,
              fixedrange: true
            },
            annotations: annotations
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: [
              "sendDataToCloud",
              "select2d",
              "lasso2d",
              "hoverClosestCartesian",
              "hoverCompareCartesian",
              "toggleSpikelines"
            ]
          }}
          useResizeHandler={true}
          onUpdate={function (graphDiv) {
            // Initial ranges don't get reset when the graph updates,
            // causing problems when changing filter parameters then
            // trying to reset zoom levels. This callback makes sure
            // that the initial ranges get updated whenever the graph
            // changes.
            graphDiv._fullLayout.xaxis._rangeInitial = [attributeValue.xMin, attributeValue.xMax];
            graphDiv._fullLayout.yaxis._rangeInitial = [attributeValue.yMin, attributeValue.yMax];
          }}
        />
      </div>
    );
  };
}

function mapStateToProps(state) {
  const { user } = state.authentication;
  return { user };
}

export default connect(mapStateToProps)(AlertLineGraph);



// WEBPACK FOOTER //
// ./src/_components/AlertLineGraph/index.js